import React, { createContext, useContext, useState, useEffect } from 'react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

const TeamPortalAuthContext = createContext(null)

export function TeamPortalAuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('portal_token'))

  useEffect(() => {
    if (token) {
      fetchUser()
    } else {
      setLoading(false)
    }
  }, [token])

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      
      if (res.ok) {
        const data = await res.json()
        setUser(data)
      } else {
        logout()
      }
    } catch (err) {
      console.error('Auth error:', err)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Environment': 'production'
      },
      body: JSON.stringify({ email, password })
    })
    
    if (!res.ok) {
      const error = await res.json()
      throw new Error(error.detail || 'Login failed')
    }
    
    const data = await res.json()
    const accessToken = data.access_token
    
    // Get user data
    const meRes = await fetch(`${API_URL}/api/auth/me`, {
      headers: { 
        'Authorization': `Bearer ${accessToken}`,
        'X-Environment': 'production'
      }
    })
    
    if (!meRes.ok) {
      throw new Error('Failed to verify user')
    }
    
    const userData = await meRes.json()
    
    localStorage.setItem('portal_token', accessToken)
    setToken(accessToken)
    setUser(userData)
    
    return userData
  }

  const loginWithGoogle = () => {
    // Redirect to Google OAuth
    const redirectUri = encodeURIComponent(`${window.location.origin}/portal/auth/callback`)
    window.location.href = `${API_URL}/api/auth/google?redirect_uri=${redirectUri}&portal=true`
  }

  const handleOAuthCallback = async (accessToken) => {
    // Get user data after OAuth callback
    const meRes = await fetch(`${API_URL}/api/auth/me`, {
      headers: { 
        'Authorization': `Bearer ${accessToken}`,
        'X-Environment': 'production'
      }
    })
    
    if (!meRes.ok) {
      throw new Error('Failed to verify user')
    }
    
    const userData = await meRes.json()
    
    localStorage.setItem('portal_token', accessToken)
    setToken(accessToken)
    setUser(userData)
    
    return userData
  }

  const logout = () => {
    localStorage.removeItem('portal_token')
    setToken(null)
    setUser(null)
  }

  return (
    <TeamPortalAuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      loginWithGoogle,
      handleOAuthCallback,
      logout 
    }}>
      {children}
    </TeamPortalAuthContext.Provider>
  )
}

export function useTeamPortalAuth() {
  return useContext(TeamPortalAuthContext)
}
